import { Users } from 'lucide-react';
import { memo, useCallback } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';

// Pre-define styles as constants to avoid recreating them on every render
const BUTTON_STYLES = {
  base: 'h-full w-full rounded font-bold',
  active:
    'border-2 border-red-700 bg-red-100 text-red-800 shadow-sm dark:bg-red-950/50 dark:text-red-300 dark:border-red-800',
  inactive:
    'bg-gray-100/80 text-gray-400 dark:bg-gray-800/30 dark:text-gray-500',
  focus:
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50 focus-visible:ring-offset-1',
};

type Props = {
  item: IOneItemInPrepareForRequest;
  handleSelectItem: (item: IOneItemInPrepareForRequest) => void;
  handleShowUnderRequest: (value: boolean) => void;
};

function UnderRequestCol({
  item,
  handleSelectItem,
  handleShowUnderRequest,
}: Props) {
  const totalUnderRequests = item?.totalUnderRequests || 0;
  const hasRequests = totalUnderRequests > 0;

  // Memoized click handler to prevent recreation on each render
  const handleClick = useCallback(() => {
    if (hasRequests) {
      handleSelectItem(item);
      handleShowUnderRequest(true);
    }
  }, [hasRequests, handleSelectItem, handleShowUnderRequest, item]);

  return (
    <div className="flex h-16 w-full items-center justify-center">
      <Button
        className={cn(
          BUTTON_STYLES.base,
          BUTTON_STYLES.focus,
          hasRequests ? BUTTON_STYLES.active : BUTTON_STYLES.inactive,
        )}
        variant="ghost"
        disabled={!hasRequests}
        onClick={handleClick}
        aria-label={
          hasRequests ? `عرض ${totalUnderRequests} طلبات` : 'لا توجد طلبات'
        }
      >
        <div className="flex items-center justify-center gap-1.5">
          {hasRequests && <Users className="h-4 w-4" />}
          <span className="text-xl">
            {hasRequests ? totalUnderRequests : '-'}
          </span>
        </div>
      </Button>
    </div>
  );
}

// Use custom equality function to prevent unnecessary re-renders
export default memo(UnderRequestCol, (prevProps, nextProps) => {
  return (
    prevProps.item?.totalUnderRequests === nextProps.item?.totalUnderRequests &&
    prevProps.item?.itemBodyID === nextProps.item?.itemBodyID
  );
});
